import { Box, Button, Divider, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { type ReactNode } from 'react'
import { useAuth } from '../../../../hooks/use-auth'
import { AppRoutes } from '../../../Routes'
import { GoogleIcon } from '../../../../assets/svgs/GoogleIcon'
import { MicrosoftIcon } from '../../../../assets/svgs/MicrosoftIcon'
import { P2PAuthShell } from './P2PAuthShell'
import { styles } from './P2PAuthLanding.styles'

/**
 * START-owned "Start Here" landing for invited Peer-to-Peer users. START does NOT render
 * credential input fields or mint a session — every option redirects into MiCredentials'
 * hosted flow (Google/Microsoft/MiCredentials; MiDataHub via MiLaunchPad). On successful
 * auth the CurrentUser seam auto-provisions the invited user and routes to collection.
 * Email/password fields await a MiCredentials credential-submission API and are
 * intentionally not built here.
 */
export const P2PAuthLanding = (): JSX.Element => {
  const auth = useAuth()
  const returnUrl = AppRoutes.PEER_TO_PEER

  const providers: Array<{ key: string, label: string, icon?: ReactNode, onClick: () => void }> = [
    { key: 'midatahub', label: 'Access with MiDataHub', onClick: () => { auth.miLaunchPadLogin(returnUrl) } },
    { key: 'google', label: 'Access with Google', icon: <GoogleIcon />, onClick: () => { auth.miCredsLogin(returnUrl) } },
    { key: 'microsoft', label: 'Access with Microsoft', icon: <MicrosoftIcon />, onClick: () => { auth.miCredsLogin(returnUrl) } }
  ]

  return (
    <P2PAuthShell>
      <Box data-testid='p2pAuthLanding'>
        <Typography variant='h1' sx={styles.title}>Start Here</Typography>
        <Typography variant='body1' sx={styles.subtitle}>Access your form using one of the options below.</Typography>

        <Box sx={styles.providerList}>
          {providers.map(provider => (
            <Button
              key={provider.key}
              variant='outlined'
              fullWidth
              startIcon={provider.icon}
              onClick={provider.onClick}
              data-testid={`p2pAuthProvider-${provider.key}`}
              sx={styles.providerButton}
            >
              {provider.label}
            </Button>
          ))}
        </Box>

        <Divider sx={{ my: '20px' }}>or</Divider>

        <Typography variant='h4' sx={styles.micredsHeading}>Sign in with your MiCredentials account</Typography>
        <Button
          variant='contained'
          color='primary'
          fullWidth
          endIcon={<ArrowForwardIcon />}
          onClick={() => { auth.miCredsLogin(returnUrl) }}
          data-testid='p2pAuthProvider-micredentials'
          sx={styles.micredsButton}
        >
          Get Started
        </Button>

        <Typography variant='caption' color='text.secondary' sx={styles.footnote}>
          Account creation, sign-in, and password reset are handled securely by MiCredentials. START never stores your password.
        </Typography>
      </Box>
    </P2PAuthShell>
  )
}
